import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  cancelSubscription as apiCancel,
  createSubscription as apiCreate,
  fetchSubscription as apiFetch,
  fetchSubscriptionPlans as apiPlans,
  resumeSubscription as apiResume,
} from '../utils/heywaitrPlusApi';
import { isSubscriptionActive } from '../utils/heywaitrPlus';

/**
 * Manages a user's HeyWaitr+ subscription state and lifecycle actions. Shared by
 * the deals page (to gate access) and the Profile page (to manage billing).
 *
 * Available plans are loaded live from /subscription/getSubscriptionList — there
 * is no hard-coded plan id. The deals page subscribes to the "primary" (monthly)
 * plan; the Profile page lets the user pick any plan from the list.
 *
 * @param {string} [token] bearer token; when absent the user is treated as
 *   logged-out and no subscription network calls are made.
 * @param {boolean} [initialSubscribed] optimistic value from the cached profile,
 *   used until the live /subscription/fetch resolves.
 */
export default function useSubscription(token, initialSubscribed = false) {
  const [subscription, setSubscription] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(!!initialSubscribed);
  const [loading, setLoading] = useState(!!token);
  const [actionLoading, setActionLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    if (!token) {
      setSubscription(null);
      setIsSubscribed(false);
      setLoading(false);
      return null;
    }
    try {
      const data = await apiFetch(token);
      if (!mountedRef.current) return data;
      setSubscription(data || null);
      setIsSubscribed(isSubscriptionActive(data));
      return data;
    } catch (e) {
      // Keep the optimistic value on transient errors rather than flipping the UI.
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token]);

  // Load the available plans (public endpoint). Returns the list so callers that
  // need it before the next render (e.g. subscribe()) can await it directly.
  const loadPlans = useCallback(async () => {
    try {
      const data = await apiPlans();
      const list = Array.isArray(data) ? data : [];
      if (mountedRef.current) setPlans(list);
      return list;
    } catch (e) {
      return [];
    } finally {
      if (mountedRef.current) setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    loadPlans();
    return () => {
      mountedRef.current = false;
    };
  }, [refresh, loadPlans]);

  // The plan the deals-page CTA subscribes to: prefer the monthly plan, else the
  // first plan returned by the backend.
  const primaryPlan = useMemo(() => {
    if (!plans.length) return null;
    return plans.find((p) => /month/i.test(p.id)) || plans[0];
  }, [plans]);

  // Start checkout for a plan, then redirect to the Reepay hosted-payment URL.
  // `planArg` may be a plan object, a plan id string, or omitted (⇒ primary plan).
  const subscribe = useCallback(
    async (planArg, { acceptUrl, cancelUrl } = {}) => {
      if (!token) throw new Error('NOT_LOGGED_IN');

      let plan = planArg;
      if (!plan) {
        plan = primaryPlan || (await loadPlans()).find((p) => /month/i.test(p.id));
      }
      const planId = typeof plan === 'string' ? plan : plan?.id;
      if (!planId) throw new Error('NO_PLAN');

      setActionLoading(true);
      try {
        const origin = window.location.origin;
        const url = await apiCreate(token, {
          plan: planId,
          acceptUrl: acceptUrl || `${origin}/heywaitr-plus?sub=success`,
          cancelUrl: cancelUrl || `${origin}/heywaitr-plus`,
        });
        // Only redirect to a real absolute checkout URL; otherwise surface an error.
        if (typeof url === 'string' && /^https?:\/\//i.test(url)) {
          window.location.href = url;
          return url;
        }
        // eslint-disable-next-line no-console
        console.error('[heywaitr+] Unexpected /subscription/create response:', url);
        throw new Error('INVALID_CHECKOUT_URL');
      } finally {
        if (mountedRef.current) setActionLoading(false);
      }
    },
    [token, primaryPlan, loadPlans],
  );

  const cancel = useCallback(async () => {
    if (!token) return null;
    setActionLoading(true);
    try {
      const data = await apiCancel(token);
      if (mountedRef.current) {
        setSubscription(data || null);
        setIsSubscribed(isSubscriptionActive(data));
      }
      return data;
    } finally {
      if (mountedRef.current) setActionLoading(false);
    }
  }, [token]);

  const resume = useCallback(async () => {
    if (!token) return null;
    setActionLoading(true);
    try {
      const data = await apiResume(token);
      if (mountedRef.current) {
        setSubscription(data || null);
        setIsSubscribed(isSubscriptionActive(data));
      }
      return data;
    } finally {
      if (mountedRef.current) setActionLoading(false);
    }
  }, [token]);

  return {
    subscription,
    isSubscribed,
    loading,
    actionLoading,
    plans,
    plansLoading,
    primaryPlan,
    refresh,
    subscribe,
    cancel,
    resume,
  };
}
